import { BaseButton } from '@flippingbook/web.react-kit/dist/BaseButton';
import { noop } from '@flippingbook/web.react-kit/dist/utils/noop';
import cx from 'classnames';
import { forwardRef } from 'react';

import { b } from './style.css';

type ButtonCommonProps = {
    theme: 'blue' | 'green' | 'yellow' | 'red';
    size: 'small' | 'medium' | 'large';
};

type ButtonTypeProps = {
    type: 'submit' | 'reset' | 'button';
};

type ButtonProps = React.ComponentPropsWithoutRef<'button'> & Partial<ButtonCommonProps> & Partial<ButtonTypeProps>;

type LinkButtonProps = React.ComponentPropsWithoutRef<'a'> & Partial<ButtonCommonProps>;

const getClassName = ({
    classNameProp,
    size,
    theme,
}: {
    theme: ButtonCommonProps['theme'];
    size?: ButtonCommonProps['size'];
    classNameProp?: string;
}) =>
    cx(
        b.name,
        b.m[theme],
        {
            [b.m.small]: size === 'small',
            [b.m.medium]: size === 'medium',
            [b.m.large]: size === 'large',
        },
        classNameProp,
    );

export const InlineButton = forwardRef<HTMLButtonElement, ButtonProps>(function InlineButton(props, ref) {
    const {
        onClick = noop,
        onMouseDown = noop,
        theme = 'blue',
        type = 'button',
        size,
        disabled = false,
        className: classNameProp,
        children,
        ...otherProps
    } = props;

    const className = getClassName({
        classNameProp,
        size,
        theme,
    });

    return (
        <BaseButton
            {...otherProps}
            ref={ref}
            className={className}
            type={type}
            onClick={disabled ? noop : onClick}
            onMouseDown={disabled ? noop : onMouseDown}
            disabled={disabled}
        >
            {children}
        </BaseButton>
    );
});
InlineButton.displayName = 'InlineButton';

export const InlineLinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(function InlineLinkButton(props, ref) {
    const { theme = 'blue', size, className: classNameProp, children, ...otherProps } = props;

    const className = getClassName({
        classNameProp,
        size,
        theme,
    });

    return (
        <a
            {...otherProps}
            ref={ref}
            className={className}
        >
            {children}
        </a>
    );
});
InlineLinkButton.displayName = 'InlineLinkButton';
